'use client';

import type { PropsWithChildren } from 'react';

import { type DehydratedState, HydrationBoundary } from '@tanstack/react-query';

import AuthProvider from '@shared/common/providers/AuthProvider';
import { CompositeProvider } from '@shared/common/providers/CompositeProvider';
import GlobalModalsProvider from '@shared/common/providers/GlobalModalsProvider';
import { GlobalStateProvider } from '@shared/common/providers/GlobalStateProvider';
import GoogleTagManagerProvider from '@shared/common/providers/GoogleTagManagerProvider';
// eslint-disable-next-line deprecate/import
import LegacyWeb3Provider from '@shared/common/providers/LegacyWeb3Provider';
import { MixPanelProvider } from '@shared/common/providers/MixPanelProvider';
import { NewsletterSubscriptionProvider } from '@shared/common/providers/NewsletterSubscriptionProvider';
import PasskeyProvider from '@shared/common/providers/PasskeyProvider';
import { SocketProvider } from '@shared/common/providers/socket';
import ToastProvider from '@shared/common/providers/ToastProvider';
import TonProvider from '@shared/common/providers/TonProvider';
import TurnstileProvider from '@shared/common/providers/TurnstileProvider';
import UserAgentDetectProvider from '@shared/common/providers/UserAgentDetectProvider';

import GlobalModalsStore from '@store:web/components/providers/GlobalModalsStore';
import I18nProvider from '@store:web/components/providers/I18nProvider';
import NotificationBannerProvider from '@store:web/components/providers/NotificationBannerProvider/NotificationBannerProvider';
import ProfileMenuProvider from '@store:web/components/providers/ProfileMenuProvider/ProfileMenuProvider';
import SnapshotProvider from '@store:web/components/providers/SnapshotProvider';
import StoreChainProvider from '@store:web/components/providers/StoreChainProvider';
import StoreNavMenuProvider from '@store:web/components/providers/StoreNavMenuProvider/StoreNavMenuProvider';
import { Overlay, OverlaysProvider } from '@ui/uikit/components/common/Overlays';
import { LightweightQueryProviderSsr } from '@utilities/react-query';

interface UniversalProviderProps extends PropsWithChildren {
  dehydratedState?: DehydratedState;
}

const UniversalProvider = ({ children, dehydratedState }: UniversalProviderProps) => {
  return (
    <CompositeProvider
      providers={[
        GoogleTagManagerProvider,
        TurnstileProvider,
        TonProvider,
        LegacyWeb3Provider,
        I18nProvider,
        LightweightQueryProviderSsr,
        {
          provider: HydrationBoundary,
          properties: {
            state: dehydratedState,
          },
        },
        UserAgentDetectProvider,
        GlobalStateProvider,
        AuthProvider,
        MixPanelProvider,
        {
          provider: PasskeyProvider,
          properties: {
            appId: process.env.NEXT_PUBLIC_PASSKEY_APP_ID || '',
          },
        },
        SocketProvider,
        ToastProvider,
        SnapshotProvider,
        {
          provider: OverlaysProvider,
          properties: {
            layers: ['ai-agent', 'menus', 'modals'],
          },
        },
        GlobalModalsProvider,
        {
          provider: GlobalModalsStore,
          properties: {
            portalComponent: Overlay,
          },
        },
        StoreChainProvider,
        NotificationBannerProvider,
        ProfileMenuProvider,
        StoreNavMenuProvider,
        NewsletterSubscriptionProvider,
      ]}
    >
      {children}
    </CompositeProvider>
  );
};

export { UniversalProvider };
